import colors from 'src/utils/colors';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface BrandMetadata {
  id: string;
  brandId: number;
  brandName: string;
  name: string;
  parentBrandId: number | null;
  parentBrandName: string | null;
  retailerIds: number[];
  categoryIds: number[];
  categoryIdsProductCount: Record<string, any>;
  brandNameSuggestion: string | null;
  searchScoreByCategory: Record<string, any>;
  searchScore: number;
}

export interface BrandRecord {
  fieldKey: number;
  value: number;
  metadata: BrandMetadata;
}

export interface KeywordRecord {
  fieldKey: string;
  brandId: number;
  value: number;
}

export interface KeywordBrandRecord {
  fieldKey: string;
  brandId: number;
  value: number;
}

const BRAND_COLOR_PALETTE = [
  colors.lightBlue, // primaryLight
  colors.gold, // accentGold
  colors.teal, // accentTeal
  colors.brandClubGreen // success
];

const DEFAULT_COLOR = colors.darkBlue; // primary

export interface BrandTrafficState {
  // State
  brands: BrandRecord[];
  keywords: KeywordRecord[];
  keywordBrandTraffic: KeywordBrandRecord[];
  selectedBrands: number[];
  isLoadingBrands: boolean;
  isLoadingKeywords: boolean;
  isLoadingKeywordBrands: boolean;
  error: Error | null;
  lastEntityContext: { type?: string; id?: string; subTab?: string }; // Track last entity context
  brandColorMap: Map<number, string>;
  removedColors: string[]; // Track colors from removed brands
  // if user has changed the brands, we will use this flag to determine if we reset the brands when subtab changes.
  // if user changedBrand, it will be used across all tab, otherwise it will pick top 4 brands by volume for each subtab.
  userChangedBrands: boolean;

  // Actions
  setBrands: (brands: BrandRecord[]) => void;
  setKeywords: (keywords: KeywordRecord[]) => void;
  setKeywordBrandTraffic: (data: KeywordBrandRecord[]) => void;
  setIsLoadingBrands: (isLoading: boolean) => void;
  setIsLoadingKeywords: (isLoading: boolean) => void;
  setIsLoadingKeywordBrands: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  // Brand selection actions
  addBrand: (brandId: number) => void;
  removeBrand: (brandId: number) => void;
  clearSelectedBrands: () => void;
  pickTop4BrandsByVolume: () => void;
  resetOnEntityChange: (entityType?: string, entityId?: string, subTab?: string) => boolean;
  getBrandColor: (brandId: number, index: number) => string;
  resetColorMap: () => void;
  setUserChangedBrands: (userChangedBrands: boolean) => void;
  // Selectors
  getSelectedBrands: () => BrandRecord[];
  getTopBrands: (count: number) => BrandRecord[];
  getBrandById: (brandId: number) => BrandRecord | undefined;
  getKeywordsByBrandId: (brandId: number) => KeywordRecord[];
  isSelectedBrand: (brandId: number) => boolean;
}

/**
 * Zustand store for brand traffic data
 * Manages brand data, keyword data, and selections for combined brand & keyword data fetching
 */
export const useBrandTrafficStore = create<BrandTrafficState>()(
  devtools(
    (set, get) => ({
      brands: [],
      keywords: [],
      keywordBrandTraffic: [],
      selectedBrands: [],
      isLoadingBrands: false,
      isLoadingKeywords: false,
      isLoadingKeywordBrands: false,
      error: null,
      lastEntityContext: {}, // Initialize empty object to track entity context
      brandColorMap: new Map<number, string>(),
      removedColors: [],
      userChangedBrands: false,

      setBrands: (brands) => set({ brands }),
      setKeywords: (keywords) => set({ keywords }),
      setKeywordBrandTraffic: (data) => set({ keywordBrandTraffic: data }),
      setIsLoadingBrands: (isLoading) => set({ isLoadingBrands: isLoading }),
      setIsLoadingKeywords: (isLoading) => set({ isLoadingKeywords: isLoading }),
      setIsLoadingKeywordBrands: (isLoading) => set({ isLoadingKeywordBrands: isLoading }),
      setError: (error) => set({ error }),
      setUserChangedBrands: (userChangedBrands) => set({ userChangedBrands }),
      addBrand: (brandId) => {
        const { selectedBrands, isSelectedBrand } = get();

        if (!isSelectedBrand(brandId)) {
          set({ selectedBrands: [...selectedBrands, brandId] });
        }
      },

      removeBrand: (brandId) => {
        const { selectedBrands, brandColorMap } = get();

        if (brandColorMap.has(brandId)) {
          const removedColor = brandColorMap.get(brandId)!;
          set((state) => ({
            selectedBrands: selectedBrands.filter((id) => id !== brandId),
            removedColors: [...state.removedColors, removedColor]
          }));

          const newColorMap = new Map(brandColorMap);
          newColorMap.delete(brandId);
          set({ brandColorMap: newColorMap });
        } else {
          set({
            selectedBrands: selectedBrands.filter((id) => id !== brandId)
          });
        }
      },

      clearSelectedBrands: () => set({ selectedBrands: [] }),

      pickTop4BrandsByVolume: () => {
        const topBrands = get().getTopBrands(4);
        const brandIds = topBrands.map((brand) => brand.fieldKey);

        set({ selectedBrands: brandIds });
      },

      getBrandColor: (brandId, index) => {
        const { brandColorMap, removedColors } = get();

        // If brand already has a color, return it
        if (brandColorMap.has(brandId)) {
          return brandColorMap.get(brandId)!;
        }

        // Try to use a color from the removed colors pool first
        if (removedColors.length > 0) {
          const recycledColor = removedColors[0];

          // Update state by removing the used color from removedColors
          set((state) => ({
            brandColorMap: new Map(state.brandColorMap).set(brandId, recycledColor),
            removedColors: state.removedColors.slice(1) // Remove the first color
          }));

          return recycledColor;
        }

        // If no recycled colors available, use the original logic
        const assignedColors = Array.from(brandColorMap.values());
        const availableColor = BRAND_COLOR_PALETTE.find((color) => !assignedColors.includes(color));

        const color = availableColor || BRAND_COLOR_PALETTE[index % BRAND_COLOR_PALETTE.length] || DEFAULT_COLOR;

        set((state) => ({
          brandColorMap: new Map(state.brandColorMap).set(brandId, color)
        }));

        return color;
      },

      resetColorMap: () => set({ brandColorMap: new Map(), removedColors: [] }),

      resetOnEntityChange: (entityType, entityId, subTab) => {
        const { lastEntityContext, userChangedBrands } = get();

        if (!entityType || !entityId || !subTab) return false;

        const hasContextChanged = lastEntityContext.type !== entityType || lastEntityContext.id !== entityId;

        if (hasContextChanged) {
          set({
            selectedBrands: [],
            brandColorMap: new Map(),
            removedColors: [],
            lastEntityContext: { type: entityType, id: entityId, subTab }
          });
          return true;
        }

        if (userChangedBrands) {
          return false;
        } else {
          set({
            selectedBrands: [],
            brandColorMap: new Map(),
            removedColors: [],
            lastEntityContext: { type: entityType, id: entityId, subTab }
          });
          return true;
        }
      },

      getSelectedBrands: () => {
        const { brands, selectedBrands } = get();

        return brands.filter((brand) => selectedBrands.includes(brand.fieldKey));
      },

      getTopBrands: (count) => {
        const { brands } = get();

        return [...brands].sort((a, b) => b.value - a.value).slice(0, count);
      },

      getBrandById: (brandId) => {
        return get().brands.find((brand) => brand.fieldKey === brandId);
      },

      getKeywordsByBrandId: (brandId) => {
        return get().keywords.filter((keyword) => keyword.brandId === brandId);
      },

      isSelectedBrand: (brandId) => {
        return get().selectedBrands.includes(brandId);
      }
    }),
    { name: 'brand-traffic-store' }
  )
);
